import * as React from "react";
import { DiscordSDK } from "@discord/embedded-app-sdk";
import { MockDiscordSDK } from "~lib/MockDiscordSDK";
import { useState, useEffect, useContext, createContext } from "react";
import {
  Connection,
  DataFromType,
  SyncStage,
  TypedListener,
} from "~shared/Connection";
import { MessageType, Namespace } from "~shared/messageTypes";
import { BaseClient, BaseServer } from "~shared/namespace/Base";
import { useLastGameStartTime } from "~/App";

const discordSdk =
  import.meta.env.PROD && window.location.hostname !== "localhost"
    ? new DiscordSDK(import.meta.env.VITE_DISCORD_CLIENT_ID)
    : new MockDiscordSDK();
const ConnectionContext = createContext<Connection | undefined>(undefined);

export abstract class ConnectionWrapper {
  private base: Connection;
  private handlerIds: number[] = [];

  constructor(base: Connection) {
    this.base = base;
  }

  handleMessageWithType<Type extends MessageType>(
    type: Type,
    handler: (data: DataFromType<Type>) => void
  ) {
    this.handlerIds.push(this.base.handleMessageWithType(type, handler));
  }

  sendMessageWithType<Type extends MessageType>(
    type: Type,
    data: DataFromType<Type>
  ) {
    this.base.sendMessageWithType(type, data);
  }

  async register(timeout: number = 5000) {
    if (this.registerStage() === false) return;
    await this.base.clientSync(this.registerStage() as SyncStage, timeout);
    return this;
  }

  unregister() {
    this.handlerIds.forEach((x) => this.base.removeHandler(x));
  }

  abstract registerStage(): SyncStage | false;
}

class InfoConnectionWrapper<
  InfoMessageType extends MessageType
> extends ConnectionWrapper {
  constructor(
    base: Connection,
    value: InfoMessageType,
    setter: TypedListener<InfoMessageType>
  ) {
    super(base);
    this.handleMessageWithType(value, setter);
    this.sendMessageWithType(BaseClient.TypeValue.REQUEST_GAME_INFO, void 0);
  }

  registerStage(): SyncStage | false {
    return false;
  }
}

export const Loading = () => {
  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="loading-cards">
          <span className="loading-card card-1">♠</span>
          <span className="loading-card card-2">♥</span>
          <span className="loading-card card-3">♦</span>
          <span className="loading-card card-4">♣</span>
        </div>
        <h2 className="loading-text">Connecting to server...</h2>
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  );
};

export const TimedOut = () => {
  return (
    <div className="loading-screen">
      <div className="loading-content">
        <div className="error-icon">⚠️</div>
        <h2 className="loading-text">Connection Lost</h2>
        <p className="error-message">Lost connection to the server.</p>
      </div>
    </div>
  );
};

export const ConnectionProvider = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const [connection, setConnection] = useState<Connection | undefined>();
  useEffect(() => {
    (async () => {
      await discordSdk.ready();

      const { code } = await discordSdk.commands.authorize({
        client_id: import.meta.env.VITE_DISCORD_CLIENT_ID,
        response_type: "code",
        state: "",
        prompt: "none",
        scope: ["identify", "guilds", "applications.commands"],
      });

      const connection = new Connection(
        new WebSocket(
          import.meta.env.PROD
            ? `/api/v1/join/${code}/${discordSdk.instanceId}`
            : `${window.location.protocol === "http:" ? "ws:" : "wss:"}${
                import.meta.env.VITE_BACKEND_HOST
              }/api/v1/join/${code}/${discordSdk.instanceId}`,
          "echo-protocol"
        ),
        (state) => {
          if (state === WebSocket.OPEN) setConnection(connection);
        }
      );
    })();
  }, []);

  return <ConnectionContext value={connection}>{children}</ConnectionContext>;
};

export const useConnection = () => {
  return useContext(ConnectionContext);
};

export const useWrappedConnection = <
  T extends ConnectionWrapper,
  K extends Array<any>
>(
  enabled: boolean,
  constructor: new (connection: Connection, ...args: K) => T,
  ...args: K
) => {
  const [wrappedConnection, setWrappedConnection] = useState<T>();
  const baseConnection = useContext(ConnectionContext);
  const lastGameStartTime = useLastGameStartTime();
  useEffect(() => {
    if (!baseConnection || !enabled) return;
    const _wrapped = new constructor(baseConnection, ...args);
    _wrapped.register().then(setWrappedConnection);
    return _wrapped.unregister.bind(_wrapped);
  }, [baseConnection, enabled, lastGameStartTime]);
  return wrappedConnection;
};

export const useInfo = <InfoMessageType extends MessageType>(
  enabled: boolean,
  type: InfoMessageType
) => {
  const [info, setInfo] = useState<DataFromType<InfoMessageType>>();
  useWrappedConnection(
    enabled,
    InfoConnectionWrapper<InfoMessageType>,
    type,
    setInfo as TypedListener<InfoMessageType>
  );
  return info;
};
